import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../lib/auth';
import { useEntity } from '../../../lib/entity';
import { usePage } from '../../../lib/page';
import { useUser } from '../../../lib/user';
import discussionSearch from '../../../lib/utils/discussionSearch';
import roomSearch from '../../../lib/utils/roomSearch';
import Button from '../../Button';
import EntitiesSearch from '../EntitiesSearch';
import { Create as CreateRoomForm } from '../Forms';
import EntityAside from './EntityAside';
import EntityContent from './EntityContent';
import EntityDetails from './EntityDetails';
import EntityHeader from './EntityHeader';

export const types = {
  DISCUSSIONS: 'discussions',
  ROOMS: 'rooms',
  MESSAGES: 'messages',
};

export default function Entity({ details }) {
  const entity = useEntity().entity;
  const rooms = useEntity().rooms;
  const discussions = useEntity().discussions;
  const activeDiscussionId = useEntity().activeDiscussionId;
  const hasMessages = useEntity().hasMessages;
  const isLoading = useEntity().state.isLoading;

  const login = useAuth().login;

  const user = useUser().state.user;
  const userDispatch = useUser().dispatch;
  const requestUpdateUser = useUser().actions.requestUpdateUser;

  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleCreate = useCallback(() => {
    if (!user.id) {
      return login();
    }
    if (!user.username) {
      return requestUpdateUser(userDispatch, true, { callback: () => setIsOpen(true) });
    }
    return setIsOpen(true);
  }, [requestUpdateUser, userDispatch, login, user.id, user.username]);

  const handleClose = () => setIsOpen(false);

  const getPageRooms = () => {
    if (searchQuery.length) {
      return roomSearch(rooms, searchQuery);
    }
    return rooms;
  };

  const pageRooms = getPageRooms();

  const getPageDiscussions = () => {
    if (searchQuery.length) {
      return discussionSearch(discussions, searchQuery);
    }
    return discussions;
  };

  const pageDiscussions = getPageDiscussions().filter((d) => d.id !== activeDiscussionId);

  const { classes } = usePage();

  const [section, setSection] = useState(types.DISCUSSIONS);

  const homeSections = {
    discussions: {
      title: 'Live Discussions',
      items: pageDiscussions,
      isLoading: discussions.isLoading,
      type: types.DISCUSSIONS,
    },
    rooms: {
      title: 'Rooms',
      items: pageRooms,
      isLoading,
      type: types.ROOMS,
    },
    messages: {
      title: 'Messages',
      items: pageRooms,
      type: types.MESSAGES,
      isLoading: false,
    },
  };
  const homeSection = homeSections[section];
  const isSearching = !homeSection.items.length && !!searchQuery.length;

  const tabsClassNames = classNames('tabs', {
    'pb-4': section === types.MESSAGES || homeSection.items.length > 2 || isSearching,
  });

  classes.current.bodyMainClasses = 'space';

  const handleChange = (newSection) => setSection(newSection);

  const handleReset = () => setSearchQuery('');

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  useEffect(() => {
    const getFirstSection = () => {
      if (discussions.length) {
        return types.DISCUSSIONS;
      }

      if (hasMessages) {
        return types.MESSAGES;
      }
      return types.ROOMS;
    };
    setSection(getFirstSection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="hero">
        <EntityHeader
          title={`${entity.title}${entity.year ? ` (${entity.year})` : ''}`}
          description={!details ? entity.description : ''}
          photo={entity.photo}
          entityType={entity.type}
          onCreate={handleCreate}
          isLoading={isLoading}
        />
      </section>
      {!details && (
        <>
          <section className={tabsClassNames}>
            <section className="wrapper">
              {Object.entries(types).map(([sectionType, newType]) => {
                const buttonWrapperClasses = classNames('wrapper', {
                  underlined: newType === section,
                });
                return (
                  <section key={sectionType} className="tab">
                    <Button
                      variant="clear"
                      className={buttonWrapperClasses}
                      onClick={() => handleChange(newType)}
                    >
                      {homeSections[newType].title}
                    </Button>
                  </section>
                );
              })}
            </section>
            {(section === types.MESSAGES || homeSection.items.length > 2 || isSearching) && (
              <EntitiesSearch
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                isFilterOpen
                handleReset={handleReset}
                placeholder={`Filter ${section}`}
                showEndButton={!!searchQuery.length}
              />
            )}
          </section>
          <EntityContent
            section={section}
            onCreate={handleCreate}
            searchQuery={searchQuery}
            items={homeSection.items}
            type={homeSection.type}
            isLoading={homeSection.isLoading || isLoading}
          />
        </>
      )}
      {details && <EntityDetails />}
      <aside className="sidebar">
        <EntityAside onCreate={handleCreate} />
      </aside>
      <CreateRoomForm isOpen={isOpen} onClose={handleClose} entity={entity} />
    </>
  );
}

// Entity.whyDidYouRender = true;
